import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ControlLabel, Grid, Row, Col, Tooltip, OverlayTrigger } from 'react-bootstrap';
import clone from 'clone';
import passwordPolicy from '../../../common/passwordPolicy';
import Avatar from '../Avatar/Avatar';
import { classNames } from '../../utils/v4/utils';

const NO_LOCATION = 'No Location set';

export default class UserForm extends Component {
  getInitialState() {
    return {
      showErrors: false,
      errors: {
        name: null,
        email: null,
        firstname: null,
        lastname: null,
        password: null,
        password2: null,
      },
      zxcvbn: null,
      passwordStrength: {},
    };
  }

  state = this.getInitialState();

  componentDidMount() {
    import('zxcvbn').then(module => this.setState({ zxcvbn: module.default }));
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { showErrors } = this.state;

    if (showErrors) {
      this.validate(nextProps);
    }
  }

  onChange = event => {
    const { onEdit } = this.props;

    onEdit({ [event.target.name]: event.target.value });
  };

  onChangeName = event => {
    const { onEdit } = this.props;
    const { value } = event.target;

    if (value.includes(' ')) {
      return;
    }

    onEdit({ name: value });
  };

  onChangePassword = event => {
    const {
      user: { email, firstname, lastname, name },
      onEdit,
    } = this.props;
    const { zxcvbn } = this.state;
    const password = event.target.value;

    if (!zxcvbn) {
      return;
    }

    const passwordStrength = zxcvbn(password, [email, firstname, lastname, name]);

    this.setState({ passwordStrength }, () => onEdit({ password }));
  };

  onChangeRole = event => {
    const { onEdit } = this.props;

    onEdit({
      role_id: +event.target.value,
      role: event.target.options[event.target.selectedIndex].text,
    });
  };

  onChangeChecked = event => {
    const { onEdit } = this.props;

    onEdit({ [event.target.name]: Number(event.target.checked) });
  };

  getPasswordStrengthEstimator = () => {
    const {
      user: { password },
    } = this.props;
    const {
      passwordStrength: { score, feedback, guesses_log10, sequence },
    } = this.state;
    const { requiredLength, requiredScore } = passwordPolicy;

    if (!password) {
      return null;
    }

    const tip = feedback?.warning || '';
    const scoreText = ['high-risk', 'weak', 'okay', 'good', 'strong'][score];
    const isLong = password.length >= requiredLength;
    const isStrong = score >= requiredScore;
    const counterText =
      Math.min(password.length, requiredLength) + (password.length > requiredLength ? '+' : '');
    const counterClass = `badge badge-${isLong ? 'success' : 'danger'}`;
    const scoreClass = `badge badge-${isStrong ? 'success' : 'danger'}`;
    const patterns = Array.from(new Set(sequence?.map(item => item.pattern?.replace(/_/g, ' '))));
    const dictionaries = Array.from(
      new Set(sequence?.map(item => item.dictionary_name?.replace(/_/g, ' ')).filter(Boolean)),
    );

    return (
      <div className="mt-4 h-4">
        <span data-test="password-strength-feedback" className="password-strength-feedback">
          {tip}
        </span>
        <div className="password-strength-bar">
          <div className={`score score-${score}`} />
          <div className="stripes" />
        </div>
        <div className="password-score">
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id="tooltip-password-score">
                <div className="text-left">
                  <span className="text-light">Estimated guesses, log10:</span>{' '}
                  {guesses_log10?.toFixed(2)}
                  <br />
                  <span className="text-light">Patterns:</span> {patterns.join(', ') || 'N/A'}
                  <br />
                  <span className="text-light">Dictionaries:</span>{' '}
                  {dictionaries.join(', ') || 'N/A'}
                </div>
              </Tooltip>
            }
          >
            <span data-test="password-score" className={scoreClass}>
              {scoreText}
            </span>
          </OverlayTrigger>
          <span data-test="password-length-counter" className={counterClass}>
            {counterText}
          </span>
        </div>
      </div>
    );
  };

  getRoleOptions = () => {
    const {
      auth: {
        user: {
          user: { role, role_id },
        },
      },
      path,
      roles,
    } = this.props;

    if (path === 'user') {
      return <option value={role_id}>{role}</option>;
    }

    if (!roles || !roles.length) {
      return null;
    }

    const options = roles.map(item => (
      <option key={item.role_id} value={item.role_id}>
        {item.name}
      </option>
    ));

    options.unshift(
      <option key={-1} value={-1} disabled>
        Select a role for this user
      </option>,
    );

    return options;
  };

  getUserLocation = () => {
    const { locations, user, userLocation } = this.props;
    const location_id = user.location_id;
    const location = locations.find(item => +item.id === +location_id);

    if (!locations.length && userLocation) {
      return userLocation;
    }

    return location ? `${location.parent}${location.name}/` : NO_LOCATION;
  };

  getButtons = () => {
    const { path, onCancel } = this.props;
    const add = (
      <button
        key="1"
        data-test="button-add"
        type="submit"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        Add User
      </button>
    );
    const update = (
      <button
        key="2"
        data-test="button-update"
        type="submit"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        Update {path === 'user' ? 'Account' : 'User'}
      </button>
    );
    const cancel = (
      <button
        type="button"
        key="3"
        data-test="button-cancel"
        onClick={onCancel}
        className="px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs text-gray-400 hover:text-black transition-colors border border-transparent hover:border-gray-200"
      >
        Cancel
      </button>
    );

    return path === 'new' ? [add, cancel] : [update, cancel];
  };

  validate = nextProps => {
    const props = nextProps || this.props;
    const { user_id, name, email, firstname, lastname, password, password2, role } = props.user;
    const {
      auth: {
        user: {
          permissions: { Admin },
        },
      },
    } = props;
    const {
      passwordStrength: { score },
    } = this.state;
    const { requiredLength, requiredScore } = passwordPolicy;
    const errors = clone(this.getInitialState().errors);
    const tips = {
      required: 'This field cannot be empty.',
      invalid: 'Invalid data.',
      unique: 'This is already in use.',
      match: 'Passwords should match.',
      short: 'Password is short.',
      weak: 'Password is weak.',
    };

    // Only admins can edit name/email - only admins get info to check emails unique
    if (Admin) {
      // name
      if (!name.trim()) {
        errors.name = tips.required;
      } else if (!this.isUnique(props, 'name')) {
        errors.name = tips.unique;
      }

      // email
      if (!email.trim()) {
        errors.email = tips.required;
      } else if (!this.isEmail(email)) {
        errors.email = tips.invalid;
      } else if (!this.isUnique(props, 'email')) {
        errors.email = tips.unique;
      }
    }

    // first and lastname
    if (!firstname.trim()) {
      errors.firstname = tips.required;
    }

    if (!lastname.trim()) {
      errors.lastname = tips.required;
    }

    // password
    if (password.length) {
      if (password.length < requiredLength) {
        errors.password = tips.short;
      } else if (score < requiredScore) {
        errors.password = tips.weak;
      }
    }

    if (user_id < 0) {
      if (!password) {
        errors.password = tips.required;
      }

      if (!password2) {
        errors.password2 = tips.required;
      }
    }

    if (password !== password2) {
      errors.password2 = tips.match;
    }

    // role
    if (!role) {
      errors.role = tips.required;
    }

    // show the errors
    this.setState({ showErrors: true, errors });

    // return false on any error
    return !Object.keys(errors).find(key => errors[key]);
  };

  submit = event => {
    const { path, onUpdateMe, onUpdate, onAdd } = this.props;

    event.preventDefault();

    if (!this.validate()) {
      return;
    }

    switch (path) {
      case 'user':
        onUpdateMe();
        break;
      case ':id':
        onUpdate();
        break;
      default:
        onAdd();
    }
  };

  isUnique = (props, type) => {
    const { users, user } = props;
    const newData = user[type].toLowerCase().trim();

    return !users.some(item => {
      const oldData = item[type].toLowerCase().trim();

      return +item.user_id !== +user.user_id && newData === oldData;
    });
  };

  isEmail = email =>
    email.match(
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
    );

  isPermissionSet = permission => {
    const { roles, user } = this.props;
    const role = roles.find(item => +item.role_id === +user.role_id);

    return role ? role.perm.includes(permission) : false;
  };

  isCommanderRole = () => this.isPermissionSet('Commander');

  isDeployerRole = () => this.isPermissionSet('Deployer');

  isCurrentUserOrNotAdmin = () => {
    const {
      auth: {
        user: {
          permissions: { Admin },
        },
      },
      path,
    } = this.props;

    if (path === 'user' || !Admin) {
      return true;
    }

    return false;
  };

  shouldShowMfa = () => {
    const { auth, path } = this.props;
    const isMfaEnabledForClient = auth.user.client.mfa > 0;
    const isNotNewUser = path !== 'new';

    return isMfaEnabledForClient && isNotNewUser;
  };

  errorClass = error => (error ? 'error' : null);

  passwordPlaceholder = user => {
    const { zxcvbn } = this.state;
    let placeholder = '';

    if (!zxcvbn) {
      placeholder = 'Loading password strength estimator, please wait...';
    } else if (user.user_id > 0) {
      placeholder = 'To change the password, type a new one, otherwise leave this blank.';
    }

    return placeholder;
  };

  render() {
    const {
      auth: {
        user: {
          userid,
          permissions: { Admin },
          user: { isSSO },
        },
      },
      user,
      onClickUserLocation,
      onClickAvatar,
      onClickMfa,
    } = this.props;
    const { errors, zxcvbn } = this.state;
    const {
      user_id,
      role_id = -1,
      name = '',
      email = '',
      firstname = '',
      lastname = '',
      password = '',
      password2 = '',
      promptPasswordChange = false,
    } = user;
    const owner = userid === user_id;

    return (
      <div className="step-container mt-2 rounded-[2.5rem] border border-gray-200 bg-white p-6 shadow-[0_20px_50px_rgba(0,0,0,0.05)] md:p-8 lg:col-span-8">
        <form className="space-y-6" onSubmit={this.submit}>
          <fieldset disabled={Boolean(isSSO)}>
            <Grid fluid>
              <Row>
                <Col md={6} className="space-y-6">
                  <div>
                    {this.errorClass(errors.name) && (
                      <span className="text-sunrise font-bold  mt-1 mb-4">{errors.name}</span>
                    )}
                    <div
                      className={classNames(
                        'relative group',
                        this.errorClass(errors.name) ? 'mt-2' : '',
                      )}
                    >
                      <label
                        htmlFor="user-input-name"
                        className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                      >
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="user-input-name"
                        data-test="input-name"
                        type="text"
                        name="name"
                        value={name}
                        onChange={this.onChangeName}
                        disabled={!Admin}
                        className="w-full text-dark-night text-xl px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    {this.errorClass(errors.email) && (
                      <span className="text-sunrise font-bold  mt-1 mb-4">{errors.email}</span>
                    )}
                    <div
                      className={classNames(
                        'relative group',
                        this.errorClass(errors.email) ? 'mt-2' : '',
                      )}
                    >
                      <label
                        htmlFor="email-input"
                        className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                      >
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email-input"
                        data-test="input-email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={this.onChange}
                        disabled={!Admin}
                        className="w-full text-dark-night text-xl  px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    {this.errorClass(errors.firstname) && (
                      <span className="text-sunrise font-bold  mt-1 mb-4">{errors.firstname}</span>
                    )}
                    <div
                      className={classNames(
                        'relative group',
                        this.errorClass(errors.firstname) ? 'mt-2' : '',
                      )}
                    >
                      <label
                        htmlFor="firstname-input"
                        className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                      >
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="firstname-input"
                        data-test="input-firstname"
                        type="text"
                        name="firstname"
                        value={firstname}
                        onChange={this.onChange}
                        className="w-full text-dark-night text-xl  px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    {this.errorClass(errors.lastname) && (
                      <span className="text-sunrise font-bold  mt-1 mb-4">{errors.lastname}</span>
                    )}
                    <div
                      className={classNames(
                        'relative group',
                        this.errorClass(errors.lastname) ? 'mt-2' : '',
                      )}
                    >
                      <label
                        htmlFor="lastname-input"
                        className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                      >
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="lastname-input"
                        data-test="input-lastname"
                        type="text"
                        name="lastname"
                        value={lastname}
                        onChange={this.onChange}
                        className="w-full text-dark-night text-xl  px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                      />
                    </div>
                    <div className="mx-auto mt-4 max-w-full mb-4">
                      <div className="group relative flex items-center space-x-3 rounded-xl border border-orange-100 bg-orange-50/50 p-2 transition-all duration-300 hover:shadow-sm">
                        <div className="mt-0.5 flex-shrink-0">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100/80 text-orange-600">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="2"
                              stroke="currentColor"
                              className="h-5 w-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="mt-1 text-sm leading-relaxed text-orange-700/80 !mb-0">
                            To conform with our Strong Password Policy, you are required to use a
                            sufficiently strong and at least 8 characters long password.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <span className="text-sunrise font-bold  mt-1 mb-4" data-test="password-errors">
                      {this.errorClass(errors.password) && `${errors.password}`}
                    </span>
                    <div
                      className={classNames(
                        'relative group',
                        this.errorClass(errors.password) ? 'mt-2' : '',
                      )}
                    >
                      <label
                        htmlFor="password-input"
                        className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                      >
                        Password
                      </label>
                      <input
                        id="password-input"
                        data-test="input-password"
                        type="password"
                        name="password"
                        value={password}
                        placeholder={this.passwordPlaceholder(user)}
                        disabled={!zxcvbn}
                        autoComplete="new-password"
                        onChange={this.onChangePassword}
                        className="w-full text-dark-night text-xl  px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                      />
                      {this.getPasswordStrengthEstimator()}
                    </div>
                  </div>

                  <div>
                    {this.errorClass(errors.password2) && (
                      <span className="text-sunrise font-bold  mt-1 mb-4">{errors.password2}</span>
                    )}
                    <div
                      className={classNames(
                        'relative group',
                        this.errorClass(errors.password2) ? 'mt-2' : '',
                        this.errorClass(errors.password) ? 'mt-2' : '',
                      )}
                    >
                      <label
                        htmlFor="password2-input"
                        className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                      >
                        Repeat Password
                      </label>
                      <input
                        id="password2-input"
                        data-test="input-repeat-password"
                        type="password"
                        name="password2"
                        autoComplete="new-password"
                        value={password2}
                        onChange={this.onChange}
                        className="w-full text-dark-night text-xl font-bold px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                      />
                    </div>
                  </div>

                  {!this.isCurrentUserOrNotAdmin() && (
                    <div className="flex items-center">
                      <input
                        id="prompt-password-checkbox"
                        data-test="checkbox-password-change"
                        type="checkbox"
                        name="promptPasswordChange"
                        checked={promptPasswordChange}
                        className="w-4  h-4 cursor-pointer mr-8"
                        onChange={this.onChangeChecked}
                      />
                      <label htmlFor="prompt-password-checkbox" className="cursor-pointer">
                        Prompt user to change password on next login
                      </label>
                    </div>
                  )}
                  {this.shouldShowMfa() && (
                    <div className="flex space-x-2">
                      <div className="flex-1">
                        <div className="relative group">
                          <label
                            htmlFor="two-factor-auth"
                            className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                          >
                            Two-factor authentication
                          </label>
                          <input
                            id="two-factor-auth"
                            data-test="input-mfa"
                            value={user.mfa_verified ? 'enabled' : 'disabled'}
                            disabled
                            className="w-full text-dark-night text-xl font-bold px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        data-test="button-mfa-edit"
                        onClick={onClickMfa}
                        className="px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs text-gray-400 hover:text-black transition-colors border hover:text-warmth hover:border-warmth border-gray-200"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </Col>
                <Col md={6} className="space-y-6">
                  <div>
                    {this.errorClass(errors.role) && (
                      <span className="text-sunrise font-bold  mt-1 mb-4">{errors.role}</span>
                    )}
                    <div
                      className={classNames(
                        'relative group',
                        this.errorClass(errors.role) ? 'mt-2' : '',
                      )}
                    >
                      <label
                        htmlFor="role-select"
                        className="form-label group-focus-within:text-warmth absolute -top-2 left-6 bg-white px-2 text-[10px] font-black tracking-[0.15em] text-gray-400 uppercase transition-all"
                      >
                        Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        data-test="select-role"
                        name="role"
                        id="role-select"
                        value={role_id}
                        disabled={owner}
                        onChange={this.onChangeRole}
                        className=" static focus:border-warmth w-full rounded-2xl border border-gray-200 px-6 py-3 text-lg font-medium transition-all outline-none focus:ring-0"
                      >
                        {this.getRoleOptions()}
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="relative group">
                      <label
                        htmlFor="email-input"
                        className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
                      >
                        User Location
                      </label>
                      <input
                        id="location-input"
                        data-test="input-location"
                        type="email"
                        name="location_id"
                        value={this.getUserLocation()}
                        onClick={onClickUserLocation}
                        disabled={!Admin}
                        readOnly
                        className="w-full text-dark-night text-xl  px-6 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
                      />
                    </div>
                  </div>

                  <ControlLabel className="pl-[24px]">Avatar</ControlLabel>
                  <div className="avatar-holder">
                    <Avatar size={200} user={user} />
                    {owner && (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip id="upload-avatar-tooltip-helper">
                            Change Profile Picture
                          </Tooltip>
                        }
                      >
                        <span
                          data-test="upload-hover"
                          className="upload-hover"
                          onClick={onClickAvatar}
                        >
                          <i className="material-icons">photo_camera</i>
                        </span>
                      </OverlayTrigger>
                    )}
                  </div>
                </Col>
              </Row>
            </Grid>
            <div className="pt-6 flex flex-col sm:flex-row gap-4">{this.getButtons()}</div>
          </fieldset>
        </form>
      </div>
    );
  }
}

UserForm.propTypes = {
  auth: PropTypes.object.isRequired,
  user: PropTypes.object,
  users: PropTypes.array,
  roles: PropTypes.array,
  locations: PropTypes.array,
  path: PropTypes.string.isRequired,
  onClickUserLocation: PropTypes.func.isRequired,
  onClickAvatar: PropTypes.func.isRequired,
  onClickMfa: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onUpdateMe: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  userLocation: PropTypes.string.isRequired,
};
